type ClientApiFetchOptions = RequestInit & {
  baseUrl?: string;
  redirectOnUnauthorized?: boolean;
};

export class ClientApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message);
  }
}

export async function clientApiFetch<T>(
  path: string,
  {
    baseUrl = "",
    body,
    headers,
    redirectOnUnauthorized = true,
    ...options
  }: ClientApiFetchOptions = {},
): Promise<T> {
  const response = await fetch(resolveClientUrl(path, baseUrl), {
    ...options,
    body,
    cache: options.cache ?? "no-store",
    credentials: options.credentials ?? "same-origin",
    headers: buildHeaders(headers, body),
  });
  const responseBody = await parseResponseBody(response);

  if (!response.ok) {
    const message = getErrorMessage(responseBody, response.statusText);

    if (
      response.status === 401 &&
      redirectOnUnauthorized &&
      typeof window !== "undefined"
    ) {
      window.location.assign("/sessao-expirada");
    }

    if (
      typeof window !== "undefined" &&
      isMaintenanceResponse(response.status, responseBody)
    ) {
      window.dispatchEvent(
        new CustomEvent("innova:api-error", {
          detail: { code: "SYSTEM_MAINTENANCE" },
        }),
      );
    }
    throw new ClientApiError(message, response.status, responseBody);
  }

  return responseBody as T;
}

function isMaintenanceResponse(status: number, body: unknown) {
  return status === 503 && isRecord(body) && body.code === "SYSTEM_MAINTENANCE";
}

function resolveClientUrl(path: string, baseUrl: string) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  return `${baseUrl}${path}`;
}

function buildHeaders(
  headers: HeadersInit | undefined,
  body: BodyInit | null | undefined,
) {
  const requestHeaders = new Headers(headers);

  if (!requestHeaders.has("Accept")) {
    requestHeaders.set("Accept", "application/json");
  }

  if (body && typeof body === "string" && !requestHeaders.has("Content-Type")) {
    requestHeaders.set("Content-Type", "application/json");
  }

  return requestHeaders;
}

async function parseResponseBody(response: Response) {
  if (response.status === 204) {
    return null;
  }

  const text = await response.text();

  if (!text) {
    return null;
  }

  const contentType = response.headers.get("content-type") ?? "";

  if (!contentType.includes("application/json")) {
    return text;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function getErrorMessage(body: unknown, fallback: string) {
  if (isRecord(body)) {
    const message = body.message;

    if (Array.isArray(message)) {
      return message.join(" ");
    }

    if (typeof message === "string" && message.trim()) {
      return message;
    }

    if (typeof body.error === "string" && body.error.trim()) {
      return body.error;
    }
  }

  if (typeof body === "string" && body.trim()) {
    return body;
  }

  return fallback || "Não foi possível concluir a requisição.";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
